"use client";

import { client } from "@passwordless-id/webauthn";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { useRouter } from "next/navigation";
import styles from "./page.module.css";

interface PasskeyAuthProps {
    isRegisterMode: boolean;
}

export function PasskeyAuth({ isRegisterMode }: PasskeyAuthProps) {
    const t = useTranslations("home.auth");
    const router = useRouter();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleRegister = async () => {
        if (!email.trim()) {
            setError(t("errors.invalidEmail"));
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await fetch("/api/account/registration/challenge");
            const data = await response.json();
            const registration = await client.register({
                ...data,
                timeout: 60_000,
                user: email.trim().toLowerCase(),
            });
            const verifyResponse = await fetch(
                "/api/account/registration/verify",
                {
                    body: JSON.stringify(registration),
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                },
            );

            if (verifyResponse.ok) {
                router.push("/dashboard");
                router.refresh();
            } else {
                setError(t("errors.registrationFailed"));
            }
        } catch (err) {
            console.error("Registration error:", err);
            setError(t("errors.unknownError"));
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(
                "/api/account/authentication/challenge",
            );
            const data = await response.json();
            const authentication = await client.authenticate({
                ...data,
                timeout: 60_000,
            });
            const verifyResponse = await fetch(
                "/api/account/authentication/verify",
                {
                    body: JSON.stringify(authentication),
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                },
            );

            if (verifyResponse.ok) {
                router.push("/dashboard");
            } else {
                setError(t("errors.loginFailed"));
            }
        } catch (err) {
            console.error("Login error:", err);
            setError(t("errors.unknownError"));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isRegisterMode) {
            await handleRegister();
        } else {
            await handleLogin();
        }
    };

    return (
        <form className={styles.form} onSubmit={handleSubmit}>
            {error && <div className={styles.error}>{error}</div>}

            {isRegisterMode && (
                <div className={styles.formGroup}>
                    <label htmlFor="passkey-email" className={styles.label}>
                        {t("email")}
                    </label>
                    <input
                        id="passkey-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t("emailPlaceholder")}
                        className={styles.input}
                        disabled={loading}
                        required
                    />
                </div>
            )}

            <button type="submit" className={styles.button} disabled={loading}>
                {loading && <span className={styles.spinner} />}
                {loading
                    ? isRegisterMode
                        ? t("registering")
                        : t("loggingIn")
                    : isRegisterMode
                      ? t("register")
                      : t("login")}
            </button>
        </form>
    );
}
